import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Grid,
  Card,
  CardActions,
  useMediaQuery,
  Theme,
  useTheme
} from '@mui/material';
import { InputField, GoogleMapAutocomplete } from '@sm-dashboard/form-provider';
import { CopyAll, OpenInNew } from '@mui/icons-material';
import {
  Button,
  FieldAppearance,
  IconButton,
  Loader,
  Switch,
  Typography,
  type IconButtonColor,
  Dialog
} from '@sm-dashboard/ui-components';
import {
  Delete as DeleteIcon,
  AddAPhoto as AddAPhotoIcon,
  Language as LanguageIcon,
  Facebook as FacebookIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAppContext } from '@/context/context';
import { isWorldShalichUser } from '@/constants';
import { useNavigate } from 'react-router-dom';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';

const FORM_FIELD_FACEBOOK_BLUE = '#1877F2';

const cardBasicStyle = {
  borderRadius: 1,
  p: { xs: 2.5, md: 4 },
  mb: 2.5,
  border: '1px solid'
};

// אובייקט הסגנונות המשותפים
export const formStyles = {
  // סגנון הכרטיסים
  card: {
    ...cardBasicStyle,
    backgroundColor: 'common.white',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: 'divider'
  },

  // סגנון הכרטיסים - מצב לא ציבורי (אפור)
  cardPrivate: {
    ...cardBasicStyle,
    backgroundColor: 'grey.A100',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    borderColor: 'grey.A100',
    opacity: 0.8
  },

  // סגנון לתמונות באפור (מצב לא ציבורי)
  imagePrivate: {
    filter: 'grayscale(1) brightness(0.8)',
    opacity: 0.7
  },

  // סגנון לכפתור ניקוי
  clearButton: {
    position: 'absolute',
    top: '50%',
    right: '10px',
    transform: 'translateY(-50%)',
    zIndex: 1
  },

  // סגנון למיכל מרכזי
  centerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1
  },

  // סגנון לבוקס עם פוזיציה יחסית
  relativeBox: {
    position: 'relative'
  },

  // סגנון למלא רוחב
  fullWidth: {
    width: '100%'
  },

  cardsStack: {
    width: '100%',
    maxWidth: { xs: '95%', sm: '85%', md: '70%' },
    mx: 'auto',
    mt: 2,
    px: { xs: 2, md: 0 }
  },

  // סגנון לכרטיס רקע (Grid containers)
  sectionCard: {
    bgcolor: 'background.default',
    p: 1,
    borderRadius: 2
  },

  // סגנון לכפתור עגול
  roundedButton: {
    borderRadius: '50px',
    px: 3,
    py: 1.5,
    minWidth: '120px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      transform: 'scale(1.05)',
      transition: 'transform 0.2s ease-in-out'
    }
  },

  // סגנונות לכרטיסי סטטיסטיקה
  statisticsCard: {
    card: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      pt: 2.5,
      pb: 1.25,
      minHeight: 100,
      minWidth: { xs: '100%', sm: 160, lg: 140 },
      maxWidth: 180,
      flex: { xs: 1, lg: '1 1 140px' },
      borderRadius: 2,
      bgcolor: 'background.paper',
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 50,
      height: 50,
      borderRadius: '50%',
      bgcolor: 'grey.400',
      color: 'common.white',
      position: 'absolute',
      top: -25,
      boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
    },
    ticketsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      alignItems: 'center'
    },
    ticketsLabel: {
      display: 'block',
      color: 'text.secondary',
      fontSize: '0.7rem'
    },
    ticketsTitle: {
      fontWeight: 700,
      color: 'text.primary',
      fontSize: '0.95rem',
      textAlign: 'center',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical'
    },
    ticketsValue: {
      fontWeight: 700,
      color: 'text.primary',
      fontSize: '1.5rem',
      lineHeight: 1.2
    },
    label: {
      display: 'block',
      mb: 0.25,
      color: 'text.secondary',
      fontSize: '0.8rem'
    },
    value: {
      fontWeight: 600,
      color: 'text.primary',
      wordBreak: 'break-word'
    },
    countdownList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      alignItems: 'center'
    },
    countdownRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      direction: 'rtl'
    },
    countdownNumber: {
      fontWeight: 700,
      color: 'text.primary',
      fontSize: '1.1rem'
    },
    countdownUnit: {
      fontWeight: 400,
      color: 'text.primary',
      fontSize: '0.8rem'
    }
  },

  // סגנונות לכרטיס אירוע בלוח הרשמות
  eventCard: {
    layout: {
      display: 'flex',
      gap: 3,
      flexDirection: { xs: 'column', lg: 'row' },
      alignItems: { lg: 'stretch' },
      height: 'auto'
    },
    statsRow: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 3, sm: 2 },
      flexWrap: { sm: 'wrap', lg: 'nowrap' },
      justifyContent: { xs: 'center', sm: 'flex-start', lg: 'flex-end' },
      width: { lg: '50%' },
      alignSelf: { lg: 'flex-start' },
      ml: { lg: 'auto' },
      mt: { xs: 2, lg: 0 }
    }
  },

  // סגנון לכפתור שליחה מעוצב
  submitButton: {
    minWidth: 120,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
      transform: 'translateY(-2px)'
    }
  },

  // קו הפרדה שחור לרוחב המסך
  dividerLine: {
    width: '96%',
    height: '2px',
    position: 'relative',
    left: '2%',
    backgroundColor: 'text.primary',
    mt: 10,
    mb: 4
  },

  // תמונת ת׳אמבנייל קטנה
  regulationThumb: {
    width: 36,
    height: 36,
    objectFit: 'cover',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'grey.300',
    mr: 1
  },

  // סגנון לאנימציית ספינר
  spinAnimation: {
    '@keyframes iconSpin': {
      '0%': {
        transform: 'rotate(0deg)'
      },
      '100%': {
        transform: 'rotate(360deg)'
      }
    }
  },

  // סגנון לכפתור פעולה
  actionButton: {
    height: '45px',
    borderRadius: 3,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    fontSize: '0.875rem',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.2s ease-in-out'
    }
  },

  // סגנון לכפתור הוספה קטן
  addButton: {
    backgroundColor: 'success.main',
    color: 'white',
    '&:hover': {
      backgroundColor: 'success.dark'
    },
    width: 28,
    height: 28
  },

  // סגנון לקונטיינר flex עם גאפ
  flexContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1
  },
  flexStart: {
    display: 'flex',
    justifyContent: 'flex-start'
  },

  // סגנון לכרטיס קמפיין
  campaignCard: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 1,
    backgroundColor: 'common.white',
    border: '2px solid',
    borderColor: 'divider',
    transition: 'all 0.3s ease-in-out',
    boxShadow: 'none',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
    }
  },

  // סגנון לחלק התמונה בכרטיס
  campaignImageBox: {
    height: { xs: 120, md: 140 }, // הקטנה במובייל
    overflow: 'hidden',
    backgroundColor: 'common.white' // שינוי מ-grey.50 ללבן
  },

  // סגנון לטייטל הכרטיס
  campaignTitle: {
    px: { xs: 1, sm: 2 }, // הקטנת padding במובייל
    py: 1,
    display: 'flex',
    alignItems: 'center',
    height: '2.8em' // גובה של 2 שורות טקסט (בערך 2*line-height)
  },

  // סגנון לטקסט הטייטל
  campaignTitleText: {
    textAlign: 'center',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  },

  // סגנון לתמונה עם רווחים קטנים
  imageWithPadding: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 0
  },

  // עיצובים לספר השלוחים
  shluchimBook: {
    // כפתור אייקון צבעוני עם רקע לבן
    coloredIconButton: {
      border: '1px solid',
      borderRadius: '50%',
      width: 32,
      height: 32,
      backgroundColor: 'white', // רקע לבן לאייקון
      '& svg': {
        fill: 'currentColor'
      }
    },

    // Box לאייקון
    iconBox: {
      width: 16,
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'inherit',
      '& svg': {
        fill: ''
      }
    }
  },

  // עיצובים לטפסי מידע אישי ובתי חב"ד
  forms: {
    // שדה עם אייקון משמאל
    fieldWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: 1
    },

    // איזור flex שתופס את כל הרוחב
    flexBox: {
      flex: 1
    },

    // תצוגה מקדימה של תמונה
    imagePreviewButton: {
      p: 0,
      borderRadius: 2,
      overflow: 'hidden',
      width: '120px',
      height: '120px'
    },

    // סגנון התמונה בתצוגה מקדימה
    previewImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      borderRadius: '8px'
    },

    // כפתור מחיקה קטן
    smallDeleteButton: {
      fontSize: '0.7rem',
      height: '28px',
      borderRadius: 2
    },

    // כפתור העלאה מקווקו
    dashedUploadButton: {
      border: '2px dashed',
      borderColor: 'warning.main',
      width: 60,
      height: 60
    }
  }
};
interface FormSquareIconButtonProps {
  icon: React.ReactNode;
  tooltip?: string;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export const FormSquareIconButton: React.FC<FormSquareIconButtonProps> = ({
  icon,
  tooltip,
  to,
  onClick,
  disabled = false
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    onClick?.();
    if (to) {
      navigate(to);
    }
  };

  const button = (
    <IconButton shape="square" color="secondary" disabled={disabled} onClick={handleClick} aria-label={tooltip}>
      {icon}
    </IconButton>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
};

// קומפוננט משותף לכרטיס טופס
type FormCardFocusRequest = { id: string; nonce: number };

type FormCardCollapseContextValue = {
  enabled: boolean;
  defaultExpanded: boolean | 'first';
  claimFirst: () => boolean;
  releaseFirst: () => void;
};

type FormCardFocusContextValue = {
  enabled: boolean;
  focusRequest: FormCardFocusRequest | null;
  focusCard: (cardId: string) => void;
};

const FormCardCollapseContext = createContext<FormCardCollapseContextValue>({
  enabled: false,
  defaultExpanded: true,
  claimFirst: () => false,
  releaseFirst: () => undefined
});

const FormCardFocusContext = createContext<FormCardFocusContextValue>({
  enabled: false,
  focusRequest: null,
  focusCard: () => undefined
});

const FormCardPlainContext = createContext(false);

export const FormCardsPlainProvider = ({ children }: { children: React.ReactNode }) => (
  <FormCardPlainContext.Provider value={true}>{children}</FormCardPlainContext.Provider>
);

export const useFormCardsPlain = () => useContext(FormCardPlainContext);

const formCardFocusListeners = new Set<(cardId: string) => void>();

export const requestFormCardFocus = (cardId: string) => {
  formCardFocusListeners.forEach(listener => listener(cardId));
};

const scrollFormCardToStart = (cardId: string) => {
  const el = document.getElementById(cardId);
  if (!el) return;
  const started = performance.now();
  let lastTop = el.getBoundingClientRect().top;
  let stableFrames = 0;
  const tick = () => {
    const top = el.getBoundingClientRect().top;
    if (Math.abs(top - lastTop) < 0.5) stableFrames += 1;
    else stableFrames = 0;
    lastTop = top;
    if (stableFrames >= 3 || performance.now() - started > 700) {
      el.style.scrollMarginTop = '16px';
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.requestAnimationFrame(tick);
  };
  window.requestAnimationFrame(tick);
};

export const useFormCardsCollapse = () => useContext(FormCardFocusContext);

export const FormCardsCollapseProvider = ({
  children,
  defaultExpanded = false
}: {
  children: React.ReactNode;
  defaultExpanded?: boolean | 'first';
}) => {
  const firstClaimedRef = useRef(false);
  const [focusRequest, setFocusRequest] = useState<FormCardFocusRequest | null>(null);
  const focusCard = useCallback((cardId: string) => {
    setFocusRequest({ id: cardId, nonce: Date.now() });
    requestFormCardFocus(cardId);
  }, []);
  const value = useMemo<FormCardCollapseContextValue>(
    () => ({
      enabled: true,
      defaultExpanded,
      claimFirst: () => {
        if (defaultExpanded === true) return true;
        if (defaultExpanded !== 'first') return false;
        if (firstClaimedRef.current) return false;
        firstClaimedRef.current = true;
        return true;
      },
      releaseFirst: () => {
        firstClaimedRef.current = false;
      }
    }),
    [defaultExpanded]
  );
  const focusValue = useMemo<FormCardFocusContextValue>(
    () => ({
      enabled: true,
      focusRequest,
      focusCard
    }),
    [focusCard, focusRequest]
  );

  return (
    <FormCardCollapseContext.Provider value={value}>
      <FormCardFocusContext.Provider value={focusValue}>{children}</FormCardFocusContext.Provider>
    </FormCardCollapseContext.Provider>
  );
};

interface FormCardProps {
  title?: string;
  subTitle?: string | React.ReactNode;
  children: React.ReactNode;
  sx?: any;
  isPublic?: boolean;
  collapsible?: boolean;
  headerExtra?: React.ReactNode;
  cardId?: string;
}

const FormCardToggleIcon = ({ open }: { open: boolean }) => (
  <Box
    sx={{
      width: 31,
      height: 31,
      minWidth: 31,
      borderRadius: '50%',
      border: '0.5px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'common.white'
    }}
  >
    <Box component="span" sx={{ fontSize: 20, lineHeight: 1, mt: '-2px' }}>
      {open ? '-' : '+'}
    </Box>
  </Box>
);

export const FormCard: React.FC<FormCardProps> = ({
  title,
  subTitle,
  children,
  sx,
  isPublic = true,
  collapsible: collapsibleProp,
  headerExtra,
  cardId
}) => {
  const collapseContext = useContext(FormCardCollapseContext);
  const plain = useContext(FormCardPlainContext);
  const collapsible = plain ? false : collapsibleProp ?? (collapseContext.enabled && Boolean(title));
  const [expanded, setExpanded] = useState(() => {
    if (plain || !(collapsibleProp ?? (collapseContext.enabled && Boolean(title)))) return true;
    return collapseContext.defaultExpanded === true;
  });
  const cardStyle = isPublic ? formStyles.card : formStyles.cardPrivate;
  const titleStyle = isPublic ? 'black' : 'grey.50';
  const plainStyle = {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0,
    p: 0,
    mb: 3
  };
  const mergedSx = plain ? { ...plainStyle, ...sx } : sx ? { ...cardStyle, ...sx } : cardStyle;

  useLayoutEffect(() => {
    if (!collapsible || collapseContext.defaultExpanded !== 'first') return;
    const isFirst = collapseContext.claimFirst();
    if (isFirst) setExpanded(true);
    return () => {
      if (isFirst) collapseContext.releaseFirst();
    };
  }, [collapsible, collapseContext]);

  useEffect(() => {
    if (!collapsible) return;
    const listener = (id: string) => {
      const isTarget = Boolean(cardId && cardId === id);
      setExpanded(isTarget);
      if (!isTarget || !cardId) return;
      scrollFormCardToStart(cardId);
    };
    formCardFocusListeners.add(listener);
    return () => {
      formCardFocusListeners.delete(listener);
    };
  }, [cardId, collapsible]);

  const headerExtraNode = headerExtra ? (
    <Box
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        order: { xs: 3, md: 0 },
        flex: { xs: '1 1 100%', md: '0 0 auto' },
        justifyContent: { xs: 'flex-start', md: 'flex-end' }
      }}
    >
      {headerExtra}
    </Box>
  ) : null;

  if (!collapsible) {
    return (
      <Box sx={mergedSx}>
        {(title || headerExtraNode) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: subTitle ? 0.5 : 3
            }}
          >
            {title && <CategoryTitle title={title} sx={{ color: titleStyle, mb: 0 }} />}
            {headerExtraNode}
          </Box>
        )}
        {subTitle && (
          <Box sx={{ color: 'text.secondary', mb: 2 }}>
            <Typography variant="body">{subTitle}</Typography>
          </Box>
        )}
        {children}
      </Box>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        ...mergedSx,
        p: 0,
        overflow: 'hidden',
        mb: 2.5,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          mt: 0,
          mb: 3
        }
      }}
    >
      <AccordionSummary
        component="div"
        sx={{
          px: { xs: 2, md: 4 },
          py: { xs: 1.25, md: 2 },
          minHeight: { xs: 48, md: 64 },
          '& .MuiAccordionSummary-content': {
            m: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 0.75, md: 2 },
            width: '100%',
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }
        }}
      >
        <Box sx={{ flex: { xs: '1 1 calc(100% - 40px)', md: 1 }, minWidth: 0 }}>
          {title && (
            <CategoryTitle
              title={title}
              sx={{
                color: titleStyle,
                mb: subTitle ? 0.5 : 0,
                '& h3': { fontSize: { xs: '1rem', md: '1.25rem' }, lineHeight: 1.3 }
              }}
            />
          )}
          {subTitle && (
            <Box sx={{ color: 'text.secondary' }}>
              <Typography variant="body">{subTitle}</Typography>
            </Box>
          )}
        </Box>
        {headerExtraNode}
        <Box sx={{ order: { xs: 2, md: 0 }, flexShrink: 0 }}>
          <FormCardToggleIcon open={expanded} />
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 2.5, md: 4 },
          pb: { xs: 2.5, md: 4 },
          pt: 1,
          position: 'relative'
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

// קומפוננט משותף ללייבל שדה
interface FieldLabelProps {
  label: string;
  sx?: any;
}

export const FieldLabel: React.FC<FieldLabelProps> = ({ label, sx }) => {
  return (
    <Box sx={{ display: 'block', textAlign: 'start', mx: 2, color: 'black', ...sx }}>
      <Typography variant="h6">{label}</Typography>
    </Box>
  );
};

// קומפוננט משותף לכפתור אייקון
interface StyledIconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  tooltip?: string;
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'grey' | 'info';
  size?: 'small' | 'medium' | 'large';
  sx?: any;
  isPublic?: boolean;
}

const toDesignSystemIconButtonColor = (
  variant: NonNullable<StyledIconButtonProps['variant']>,
  isPublic: boolean
): IconButtonColor => {
  if (!isPublic) {
    return 'secondary';
  }
  switch (variant) {
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    case 'grey':
    case 'info':
      return 'secondary';
    case 'primary':
    default:
      return 'primary';
  }
};

const inferIconButtonSize = (
  sx: StyledIconButtonProps['sx'],
  explicit?: StyledIconButtonProps['size']
): 'small' | 'medium' | 'large' => {
  if (explicit) {
    return explicit;
  }
  // DS IconButton box sizes: small 25 / medium 34 / large 43 — pick nearest.
  const dimension = Number(sx?.width ?? sx?.height);
  if (!Number.isFinite(dimension)) {
    return 'medium';
  }
  const targets = [
    { size: 'small' as const, px: 25 },
    { size: 'medium' as const, px: 34 },
    { size: 'large' as const, px: 43 }
  ];
  return targets.reduce((best, t) => (Math.abs(t.px - dimension) < Math.abs(best.px - dimension) ? t : best)).size;
};

export const StyledIconButton: React.FC<StyledIconButtonProps> = ({
  onClick,
  disabled = false,
  icon,
  tooltip,
  variant = 'primary',
  size,
  sx,
  isPublic = true
}) => {
  const dsColor = toDesignSystemIconButtonColor(variant, isPublic);
  const dsSize = inferIconButtonSize(sx, size);

  const button = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx
      }}
    >
      <IconButton
        shape="circle"
        color={dsColor}
        size={dsSize}
        onClick={onClick}
        disabled={disabled}
        aria-label={tooltip}
      >
        {icon}
      </IconButton>
    </Box>
  );

  if (tooltip) {
    // Layout Box already accepts Tooltip listeners; works when the inner button is disabled too.
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
};

// קומפוננט משותף לכרטיס סקציה
interface SectionCardProps {
  children: React.ReactNode;
  sx?: any;
}

export const SectionCard: React.FC<SectionCardProps> = ({ children, sx }) => {
  return (
    <Grid container sx={{ ...formStyles.sectionCard, ...sx }}>
      {children}
    </Grid>
  );
};

// קומפוננט משותף לכותרת קטגוריה
interface CategoryTitleProps {
  title: string;
  align?: 'left' | 'center';
  sx?: any;
}

export const CategoryTitle: React.FC<CategoryTitleProps> = ({ title, align = 'left', sx }) => {
  return (
    <Box sx={{ mb: 1, textAlign: align === 'center' ? 'center' : 'start', ...sx }}>
      <Typography variant="h5">{title}</Typography>
    </Box>
  );
};

// ===== קומפוננטים משותפים חדשים =====

// קומפוננט משותף להעלאת תמונה
interface ImageUploadProps {
  currentImage?: File | string;
  readOnly?: boolean;
  onImageChange: (image: File | string | undefined) => void;
  onSaveSuccess?: () => void;
  uploadUrl: string;
  deleteUrl: string;
  imageFieldName: string; // 'branchFiles' או 'assetFiles'
  imageType?: string; // 'chabadHouseImages' או 'image'
  imageLabel: string; // 'תמונת בית חב"ד' או 'תמונת בית כנסת'
  additionalFormData?: Record<string, string>; // נתונים נוספים לשליחה
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
  loading?: boolean; // סטטוס loading מהקומפוננט האב
  isPublic?: boolean; // מצב ציבוריות לעיצוב אפור
  showSuccessMessage?: boolean; // האם להציג הודעות הצלחה נפרדות
  imageBorderRadius?: number;
  overlayDelete?: boolean;
  labelAbove?: boolean;
}

export const SharedImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  readOnly = false,
  onImageChange,
  onSaveSuccess,
  uploadUrl,
  deleteUrl,
  imageFieldName,
  imageType = 'image',
  imageLabel,
  additionalFormData = {},
  fetchUpdateData,
  loading = false,
  isPublic = true,
  showSuccessMessage = true,
  imageBorderRadius = 2,
  overlayDelete = false,
  labelAbove = false
}) => {
  const { setSnackbar } = useAppContext();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // פונקציה לקבלת URL התמונה
  const getImageSrc = () => {
    if (!currentImage) return null;
    if (typeof currentImage === 'string') return currentImage;
    return URL.createObjectURL(currentImage);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbar({
        message: 'יש להעלות קובץ תמונה בלבד',
        open: true,
        severity: 'error'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        message: 'קובץ התמונה גדול מדי',
        open: true,
        severity: 'error'
      });
      return;
    }

    setIsUploading(true);
    // עדכון מיידי של התמונה בUI - כמו בשליח
    onImageChange(file);

    try {
      // שמירה מיידית
      const formData = new FormData();
      formData.append(imageFieldName, file, imageType);

      // הוספת נתונים נוספים
      Object.entries(additionalFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await fetchUpdateData({
        method: 'POST',
        url: uploadUrl,
        data: formData
      });

      if (showSuccessMessage) {
        setSnackbar({
          open: true,
          message: 'התמונה הועלתה ונשמרה בהצלחה',
          severity: 'success'
        });
      }
      onSaveSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה בהעלאת התמונה',
        severity: 'error'
      });
      // אם נכשל, חוזרים למצב המקורי
      onImageChange(undefined);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteDialog(false);

    try {
      let data;

      if (imageFieldName === 'branchFiles') {
        // עבור Branch - שלח FormData עם השדה הנכון
        const formData = new FormData();
        formData.append('chabadHouseImages', ''); // שדה ריק למחיקה
        Object.entries(additionalFormData).forEach(([key, value]) => {
          formData.append(key, value);
        });
        data = formData;
      } else if (imageFieldName === 'familyImage') {
        // עבור Shaliach - שלח object עם השדה הנכון
        data = { familyImage: null };
      } else {
        // עבור Assets - שלח FormData
        const formData = new FormData();
        Object.entries(additionalFormData).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('image', '');
        data = formData;
      }

      await fetchUpdateData({
        method: 'POST',
        url: deleteUrl,
        data: data
      });

      onImageChange(undefined);
      if (showSuccessMessage) {
        setSnackbar({
          open: true,
          message: 'התמונה נמחקה בהצלחה',
          severity: 'success'
        });
      }
      onSaveSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה במחיקת התמונה',
        severity: 'error'
      });
      // אם נכשל במחיקה, לא נשנה כלום
    } finally {
      setIsDeleting(false);
    }
  };

  const hasImage = Boolean(getImageSrc());

  return (
    <Box
      sx={{
        pt: labelAbove ? 0 : 1,
        width: labelAbove ? '100%' : undefined,
        height: labelAbove ? '100%' : undefined,
        flex: labelAbove ? 1 : undefined,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          ...formStyles.centerContainer,
          alignItems: 'center',
          width: '100%',
          ...(labelAbove ? { height: '100%', flex: 1 } : {}),
          gap: labelAbove ? 0.5 : 1
        }}
      >
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={readOnly}
        />

        {labelAbove && (
          <Box sx={{ color: 'black', textAlign: 'center', width: '100%', mb: 0.5, flexShrink: 0 }}>
            <Typography variant="h6">{imageLabel}</Typography>
          </Box>
        )}

        {hasImage ? (
          <Box
            sx={{
              flex: labelAbove ? 1 : undefined,
              width: labelAbove ? '100%' : undefined,
              minHeight: labelAbove ? 120 : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: labelAbove ? 'center' : 'flex-start'
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {/* תצוגה מקדימה */}
              <Box
                component="button"
                type="button"
                onClick={() => setPreviewOpen(true)}
                sx={{
                  p: 0,
                  borderRadius: imageBorderRadius,
                  overflow: 'hidden',
                  width: '120px',
                  height: '120px',
                  border: 'none',
                  cursor: 'pointer',
                  bgcolor: 'transparent',
                  display: 'block'
                }}
              >
                <img
                  src={getImageSrc() || ''}
                  alt={imageLabel}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: theme.shape.borderRadius * imageBorderRadius + 'px',
                    // הוספת אפקט אפור אם הכרטיס לא ציבורי
                    ...(isPublic ? {} : formStyles.imagePrivate)
                  }}
                />
              </Box>

              {/* Loading overlay */}
              {(isDeleting || isUploading) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                >
                  {isUploading ? 'מעלה...' : 'מוחק...'}
                </Box>
              )}

              {overlayDelete && !readOnly && (
                <Box
                  onClick={event => event.stopPropagation()}
                  onMouseDown={event => event.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    insetInlineStart: 4,
                    zIndex: 2
                  }}
                >
                  <IconButton
                    shape="circle"
                    size="small"
                    color="secondary"
                    disabled={isDeleting || isUploading}
                    aria-label={`מחק ${imageLabel}`}
                    onClick={() => setDeleteDialog(true)}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            {!labelAbove && (
              <Box sx={{ textAlign: 'center', mt: 0.5, mb: overlayDelete ? 0 : 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {imageLabel}
                </Typography>
              </Box>
            )}

            {/* כפתור מחיקה בלבד */}
            {!readOnly && !overlayDelete && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  onClick={() => setDeleteDialog(true)}
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  disabled={isDeleting || isUploading}
                  fullWidth={false}
                >
                  מחק
                </Button>
              </Box>
            )}
          </Box>
        ) : labelAbove ? (
          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <StyledIconButton
              onClick={() => !readOnly && !isUploading && fileInputRef.current?.click()}
              disabled={readOnly || isUploading}
              icon={<AddAPhotoIcon />}
              tooltip={isUploading ? 'מעלה...' : `העלה ${imageLabel}`}
              isPublic={isPublic}
              variant="warning"
              size="large"
              sx={{
                opacity: isUploading ? 0.5 : 1
              }}
            />
          </Box>
        ) : (
          <>
            <StyledIconButton
              onClick={() => !readOnly && !isUploading && fileInputRef.current?.click()}
              disabled={readOnly || isUploading}
              icon={<AddAPhotoIcon />}
              tooltip={isUploading ? 'מעלה...' : `העלה ${imageLabel}`}
              isPublic={isPublic}
              variant="warning"
              size="large"
              sx={{
                opacity: isUploading ? 0.5 : 1
              }}
            />

            <Box sx={{ textAlign: 'center', mt: 0.5, mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {isUploading ? 'מעלה...' : imageLabel}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Dialog אישור מחיקה */}
      <DynamicDialog
        open={deleteDialog}
        title="אישור מחיקה"
        subtitle={`האם אתה בטוח שברצונך למחוק את ${imageLabel}?`}
        handleOkClose={handleDeleteConfirm}
        handleNotOkClose={() => setDeleteDialog(false)}
      />

      {/* Dialog תצוגה מקדימה */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth title={imageLabel}>
        {getImageSrc() && (
          <img
            src={getImageSrc() || ''}
            alt={imageLabel}
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};

// קומפוננט משותף לסוויץ פאבליק
interface PublicSwitchProps {
  isPublic: boolean;
  readOnly?: boolean;
  onToggle: (checked: boolean) => void;
  label?: string;
  placement?: 'absolute' | 'inline';
}

export const SharedPublicSwitch: React.FC<PublicSwitchProps> = ({
  isPublic,
  readOnly = false,
  onToggle,
  label = 'להציג באתר חב"ד ישראל',
  placement = 'absolute'
}) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  if (readOnly) return null;

  const isInline = placement === 'inline';

  return (
    <Box
      sx={{
        position: isInline ? 'static' : 'absolute',
        top: isInline ? undefined : 16,
        right: isInline ? undefined : 16,
        zIndex: 2,
        display: 'flex',
        flexDirection: isSmallScreen && !isInline ? 'column' : 'row',
        alignItems: 'center',
        gap: isSmallScreen && !isInline ? 0.5 : 1,
        direction: 'rtl',
        flexShrink: 0
      }}
    >
      <Box sx={{ transform: 'scaleX(-1)', flexShrink: 0 }}>
        <Switch checked={isPublic} onChange={(_e, checked) => onToggle(checked)} aria-label={label} />
      </Box>
      <Box
        sx={{
          color: 'text.primary',
          userSelect: 'none',
          whiteSpace: isInline ? { xs: 'normal', md: 'nowrap' } : undefined,
          '& h6': { fontSize: isInline ? { xs: '0.8rem', md: 'inherit' } : undefined, lineHeight: 1.3 }
        }}
      >
        <Typography variant="h6">{label}</Typography>
      </Box>
    </Box>
  );
};

// Hook משותף לסוויץ פאבליק (עבור Assets)
interface UsePublicSwitchProps {
  initialValue: boolean;
  updateUrl: string;
  assetType: string;
  onSuccess?: () => void;
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
}

export const usePublicSwitch = ({
  initialValue,
  updateUrl,
  assetType,
  onSuccess,
  fetchUpdateData
}: UsePublicSwitchProps) => {
  const { setSnackbar } = useAppContext();
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = (checked: boolean) => {
    setIsPublic(checked);

    const formData = new FormData();
    formData.append('type', assetType);
    formData.append('isPublic', checked.toString());

    fetchUpdateData({
      method: 'POST',
      url: updateUrl,
      data: formData
    })
      .then(() => {
        setSnackbar({
          open: true,
          message: `${assetType === 'בית כנסת' ? 'הבית כנסת' : 'החנות'} ${checked ? 'פורסם' : 'לא פורסם'} באתר`,
          severity: 'success'
        });
        onSuccess?.();
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'שגיאה בעדכון הסטטוס',
          severity: 'error'
        });
        setIsPublic(!checked);
      });
  };

  return { isPublic, handleToggle, setIsPublic };
};

// Hook מותאם לסוויץ פאבליק עבור בראנץ
interface UseBranchPublicSwitchProps {
  initialValue: boolean;
  updateUrl: string;
  onSuccess?: () => void;
  fetchUpdateData: (params: any) => Promise<any>; // פונקציית fetch מהקומפוננט האב
}

export const useBranchPublicSwitch = ({
  initialValue,
  updateUrl,
  onSuccess,
  fetchUpdateData
}: UseBranchPublicSwitchProps) => {
  const { setSnackbar } = useAppContext();
  const [isPublic, setIsPublic] = useState(initialValue);

  const handleToggle = async (checked: boolean) => {
    setIsPublic(checked);

    try {
      const formData = new FormData();
      formData.append('isPublic', checked.toString());

      await fetchUpdateData({
        method: 'POST',
        url: updateUrl,
        data: formData
      });

      // הודעת הצלחה מוסרה - תוצג הודעה כללית בסוף השמירה

      onSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'שגיאה בעדכון מצב הפרסום',
        severity: 'error'
      });
      // אם נכשל, חוזרים למצב הקודם
      setIsPublic(!checked);
    }
  };

  return { isPublic, handleToggle, setIsPublic };
};

// Hook משותף לGoogle Maps Autocomplete
export const useGoogleMapsAutocomplete = (initialAddress?: string) => {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [placeholder, setPlaceholder] = useState(initialAddress || 'כתובת מלאה');
  const [clearAddress, setClearAddress] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<{
    fullAddress: string;
    position?: { lat: number; lng: number };
  } | null>(null);

  const onLoad = (autocompleteLoad: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteLoad);
  };

  const onPlaceChanged = () => {
    const place = autocomplete?.getPlace();
    if (!place) return;

    let fullAddress = '';
    let position: { lat: number; lng: number } | undefined;

    if (place.name === place.vicinity) {
      fullAddress = place.name || '';
    } else {
      fullAddress = [place.name, place.vicinity].filter(Boolean).join(', ');
    }

    if (place.geometry?.location) {
      try {
        position = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
      } catch (error) {
        console.error('Error getting location:', error);
      }
    }

    setSelectedPlace({ fullAddress, position });
    if (fullAddress) setPlaceholder(fullAddress);
  };

  const handleClearAddress = () => {
    setClearAddress(true);
    setPlaceholder('');
    setSelectedPlace(null);
  };

  const getAddressData = () => {
    if (clearAddress) {
      return { fullAddress: '', position: { lat: 0, lng: 0 }, clearPosition: true };
    }

    if (selectedPlace) {
      return { fullAddress: selectedPlace.fullAddress, position: selectedPlace.position, clearPosition: false };
    }

    return { fullAddress: '', position: undefined, clearPosition: false };
  };

  const resetAddress = (newAddress?: string) => {
    setPlaceholder(newAddress || 'כתובת מלאה');
    setClearAddress(false);
    setSelectedPlace(null);
  };

  return {
    onLoad,
    onPlaceChanged,
    placeholder,
    handleClearAddress,
    getAddressData,
    resetAddress,
    clearAddress
  };
};

export const resolveFullAddressForSubmit = (
  clearPosition: boolean,
  mapsAddress: string,
  formAddress: string
): string => {
  if (clearPosition) {
    return '';
  }
  return mapsAddress || formAddress || '';
};

// קומפוננט משותף לשדה כתובת עם Google Maps
interface AddressFieldProps {
  appearance?: FieldAppearance;
  label: string;
  placeholder: string;
  readOnly?: boolean;
  onLoad: (autocomplete: google.maps.places.Autocomplete) => void;
  onPlaceChanged?: () => void;
  onClearAddress: () => void;
  showClearButton?: boolean;
}

export const SharedAddressField: React.FC<AddressFieldProps> = ({
  appearance = 'default',
  label,
  placeholder,
  readOnly = false,
  onLoad,
  onPlaceChanged,
  onClearAddress,
  showClearButton = true
}) => {
  const { userPermission } = useAppContext();

  return (
    <>
      <FieldLabel label={label} />
      <Box>
        <GoogleMapAutocomplete
          appearance={appearance}
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          placeholder={placeholder}
          label=""
          disabled={readOnly}
          worldwide={isWorldShalichUser(userPermission)}
        />
      </Box>
    </>
  );
};

// קומפוננט משותף לשדה אתר אינטרנט עם כפתור
interface WebsiteFieldProps {
  label: string;
  name: string;
  readOnly?: boolean;
  websiteValue: string;
  isPublic?: boolean;
}

export const SharedWebsiteField: React.FC<WebsiteFieldProps> = ({
  label,
  name,
  readOnly = false,
  websiteValue,
  isPublic = true
}) => {
  const theme = useTheme();
  const handleOpenWebsite = () => {
    if (websiteValue) {
      const url = websiteValue.startsWith('http') ? websiteValue : `https://${websiteValue}`;
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <FieldLabel label={label} />
      {/* תצוגה במובייל - שדה רגיל ללא אייקון */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InputField label="" name={name} readOnly={readOnly} />
      </Box>
      {/* תצוגה בדסקטופ - עם אייקון */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={formStyles.forms.fieldWithIcon}>
          <StyledIconButton
            onClick={handleOpenWebsite}
            disabled={readOnly}
            icon={<LanguageIcon />}
            tooltip="פתח אתר אינטרנט"
            variant="grey"
            isPublic={isPublic}
            sx={{
              '& .SmIconButton-root.SmIconButton-color-secondary': {
                backgroundColor: theme.palette.grey.A100,
                color: theme.palette.grey[50],
                '& .MuiSvgIcon-root': {
                  color: theme.palette.grey[50]
                }
              }
            }}
          />
          <Box sx={formStyles.forms.flexBox}>
            <InputField label="" name={name} readOnly={readOnly} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

// קומפוננט משותף לשדה פייסבוק עם כפתור
interface FacebookFieldProps {
  label: string;
  name: string;
  readOnly?: boolean;
  facebookValue: string;
  isPublic?: boolean;
}

export const SharedFacebookField: React.FC<FacebookFieldProps> = ({
  label,
  name,
  readOnly = false,
  facebookValue,
  isPublic = true
}) => {
  const handleOpenFacebook = () => {
    if (facebookValue) {
      const url = facebookValue.startsWith('http') ? facebookValue : `https://facebook.com/${facebookValue}`;
      window.open(url, '_blank');
    }
  };

  return (
    <>
      <FieldLabel label={label} />
      {/* תצוגה במובייל - שדה רגיל ללא אייקון */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InputField label="" name={name} readOnly={readOnly} />
      </Box>
      {/* תצוגה בדסקטופ - עם אייקון */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Box sx={formStyles.forms.fieldWithIcon}>
          <StyledIconButton
            onClick={handleOpenFacebook}
            disabled={readOnly}
            icon={<FacebookIcon />}
            tooltip="פתח דף פייסבוק"
            variant="primary"
            isPublic={isPublic}
            sx={
              isPublic
                ? {
                    '& .SmIconButton-root': {
                      backgroundColor: FORM_FIELD_FACEBOOK_BLUE
                    }
                  }
                : undefined
            }
          />

          <Box sx={formStyles.forms.flexBox}>
            <InputField label="" name={name} readOnly={readOnly} />
          </Box>
        </Box>
      </Box>
    </>
  );
};

// קומפוננט גנרי לכרטיסים - לא קשור לשימוש ספציפי
interface GenericCardProps {
  title: string;
  imageComponent: React.ReactNode;
  isMobile?: boolean;
  // Optional content sections
  subtitle?: string | React.ReactNode;
  subtitleIcon?: React.ReactNode;
  // Flexible actions - can be any combination
  actions?: React.ReactNode;
  // Optional footer content
  footer?: React.ReactNode;
  // Custom styling
  cardSx?: any;
  titleSx?: any;
  actionsSx?: any;
  isLoading?: boolean;
}

export const GenericCard: React.FC<GenericCardProps> = ({
  title,
  imageComponent,
  isMobile = false,
  subtitle,
  subtitleIcon,
  actions,
  footer,
  cardSx,
  titleSx,
  actionsSx,
  isLoading = false
}) => {
  return (
    <Card sx={{ ...formStyles.campaignCard, ...cardSx, position: 'relative' }}>
      {/* חלק התמונה */}
      <Box sx={formStyles.campaignImageBox}>{imageComponent}</Box>

      {/* הטייטל */}
      <Box sx={{ ...formStyles.campaignTitle, ...titleSx }}>
        <Box sx={formStyles.campaignTitleText}>
          <Typography variant="body" color="common.black">
            {title}
          </Typography>
        </Box>
      </Box>

      {/* Subtitle section - optional */}
      {subtitle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            px: { xs: 1, sm: 2 },
            pb: 1
          }}
        >
          {subtitleIcon}
          <Box
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '100px', sm: '140px' }
            }}
          >
            <Typography variant="caption" color="grey.50">
              {subtitle}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Actions section - flexible */}
      {actions && (
        <CardActions
          disableSpacing={isMobile}
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0.5,
            p: isMobile ? 1 : 1.5,
            flexWrap: 'wrap',
            justifyContent: 'center',
            ...(isMobile && {
              alignItems: 'stretch',
              width: '100%',
              '& > :not(:first-of-type)': { ml: 0, mr: 0 }
            }),
            ...actionsSx
          }}
        >
          {actions}
        </CardActions>
      )}

      {/* Footer section - optional */}
      {footer && <Box sx={{ p: { xs: 1, sm: 1.5 }, borderTop: '1px solid', borderColor: 'divider' }}>{footer}</Box>}

      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            '::before': {
              content: "''",
              position: 'absolute',
              inset: 0,
              bgcolor: 'background.paper',
              opacity: 0.3
            }
          }}
        >
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <Loader />
          </Box>
        </Box>
      )}
    </Card>
  );
};

// Helper functions for common card configurations
interface CampaignCardProps {
  title: string;
  onView: () => void;
  onCopy: () => void;
  imageComponent: React.ReactNode;
  isMobile?: boolean;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ title, onView, onCopy, imageComponent, isMobile }) => (
  <GenericCard
    title={title}
    imageComponent={imageComponent}
    isMobile={isMobile}
    actions={
      <>
        <Button color="warning" shape="square" size="small" startIcon={<OpenInNew fontSize="small" />} onClick={onView}>
          צפייה
        </Button>
        <Button color="success" shape="square" size="small" startIcon={<CopyAll fontSize="small" />} onClick={onCopy}>
          העתקה
        </Button>
      </>
    }
  />
);
